import { call, put, takeLatest } from 'redux-saga/effects';

import { Api } from '../../../entries/ApiTransport';
import sagaAssessor from '../../../utils/sagaAssessor';
import { settingsActions, settingsTypes } from './actions';

import { PaymentsApi } from '../../../entries/apis/Payments.api';
import { getShopToken } from 'Containers/Shop/store/selectors';
import { getActiveSalonToken } from 'utils/salonToken';
import { authActions } from 'Containers/Auth/store';
import { syncHasShopStorage } from 'utils/hasShopAccess';

const paymentsApi = PaymentsApi.getInstance();
const appApi = Api.getInstance();
const createCustomerPortal = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const returnUrl = location.href;
        const { clientId } = payload;
        const { data } = yield call(() =>
          paymentsApi.post('/createCustomerPortal', { returnUrl, clientId }),
        );
        yield put(settingsActions.CREATE_CUSTOMER_PORTAL.SUCCESS(data.data));
        callback?.(data);
      },
    (err) => settingsActions.CREATE_CUSTOMER_PORTAL.FAILED(err),
  );
const createPaymentPortal = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { stripeUserKey } = payload;
        const {
          data: { paymentUrl },
        } = yield call(() =>
          paymentsApi.post('/createPaymentUrl', {
            tokenApp: getActiveSalonToken(),
            stripeUserKey,
            paymentFeUrl: process.env.REACT_APP_PAYMENT_FE_ENV,
            zbBeUrl: process.env.REACT_APP_API_URL,
          }),
        );
        callback?.(paymentUrl);
      },
    (err) => settingsActions.CREATE_CUSTOMER_PORTAL.FAILED(err),
  );
const getCustomerBill = ({ payload, callback, options } = {}) =>
  sagaAssessor(
    () =>
      function* () {
        const { clientId } = payload;
        const { data } = yield call(() => paymentsApi.post('/customer', { clientId }));
        yield put(settingsActions.GET_CUSTOMER_BILL.SUCCESS(data));
        callback?.(data);
      },
    (err) => settingsActions.GET_CUSTOMER_BILL.FAILED(err),
    options,
  );

const getCustomerPlan = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { productIds } = payload;
        const { data } = yield call(() => paymentsApi.post('plans', { idsProducts: productIds }));
        yield put(settingsActions.GET_CUSTOMER_PLAN.SUCCESS(data));
        callback?.(data);
      },
    (err) =>
      settingsActions.GET_CUSTOMER_PLAN.FAILED({
        message:
          err?.message ||
          err?.response?.data?.message ||
          'Microservizio payment non raggiungibile. Verifica che sia avviato su ' +
            process.env.REACT_APP_PAYMENT_BE_ENV,
      }),
  );
const getCustomerProducts = ({ payload, callback, options }) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => appApi.post('/business/auth/getDaysLeft', {}));

        if (!data?.prezzi?.length) {
          throw new Error('Il backend non ha restituito i piani disponibili (prezzi vuoto)');
        }

        if (data?.hasPricesZero) {
          localStorage.setItem('uf_bannerPayments', 'true');
        } else {
          localStorage.setItem('uf_bannerPayments', 'false');
        }
        localStorage.setItem('currentPrice', data?.prezzo);

      
        syncHasShopStorage(data);
        yield put(settingsActions.GET_CUSTOMER_PRODUCTS.SUCCESS(data));
        yield put(authActions.SET_ID_COSTUMER_STRIPE.SUCCESS(data));

        // Allinea navbar/gate Premium alla fatturazione (serve currentPriceId Stripe).
        // Una sola volta dopo getDaysLeft — non ridispatchare da hook multipli.
        if (data?.idCustomerStripe) {
          yield put(
            settingsActions.GET_SUBSCRIPTION_STATUS.REQUEST({}, null, { skipLoader: true }),
          );
        }

        callback?.(data);
      },
    (err) =>
      settingsActions.GET_CUSTOMER_PRODUCTS.FAILED({
        message:
          err?.message ||
          err?.response?.data?.message ||
          'Backend non raggiungibile. Verifica che sia avviato su ' + process.env.REACT_APP_API_URL,
      }),
    options,
  );
const getCustomerInvoice = ({ payload, callback, options } = {}) =>
  sagaAssessor(
    () =>
      function* () {
        const { clientId } = payload;
        const { data } = yield call(() => paymentsApi.post('/invoices', { clientId }));
        yield put(settingsActions.GET_CUSTOMER_INVOICE.SUCCESS(data));
        callback?.(data);
      },
    (err) => settingsActions.GET_CUSTOMER_INVOICE.FAILED(err),
    options,
  );

const getSubscriptionStatus = ({ callback, options } = {}) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => appApi.post('/business/subscription/status', {}));
        if (
          data?.currentPeriodEnd &&
          (data.status === 'active' || data.status === 'trialing')
        ) {
          yield call(() =>
            appApi
              .post('/business/auth/syncStripeExpiration', {
                scadenza: data.currentPeriodEnd,
                stripeStatus: data.status,
              })
              .catch(() => null),
          );
        }
        yield put(settingsActions.GET_SUBSCRIPTION_STATUS.SUCCESS(data));
        callback?.(data);
      },
    (err) => settingsActions.GET_SUBSCRIPTION_STATUS.FAILED(err),
    options,
  );

const MISSING_PAYMENT_METHOD_MESSAGE =
  'Questo account non ha un metodo di pagamento predefinito. Completa il pagamento su Stripe e riprova.';

const SUBSCRIPTION_ERROR_MESSAGES = {
  subscription_outstanding_balance:
    'Impossibile aggiornare il piano: sono presenti pagamenti in sospeso. Regolarizza gli insoluti prima di procedere.',
  subscription_payment_intent_requires_action:
    'È necessario aggiornare il metodo di pagamento. Usa "Modifica metodo di pagamento" e riprova.',
  active_subscription_exists:
    'Hai già un abbonamento attivo. Aggiorna la pagina per vedere lo stato corrente.',
  resource_missing: MISSING_PAYMENT_METHOD_MESSAGE,
};

const getStripeErrorCode = (err) => {
  if (!err || typeof err === 'string') return null;
  return err.code || err.raw?.code || null;
};

const getStripeErrorText = (err) => {
  if (!err) return '';
  if (typeof err === 'string') return err;
  return String(err.raw?.message || err.message || '');
};

const isMissingPaymentMethodError = (err) => {
  const code = getStripeErrorCode(err);
  const text = getStripeErrorText(err);
  return (
    code === 'resource_missing' ||
    /no attached payment source|default payment method/i.test(text)
  );
};

const mapSubscriptionError = (rawError) => {
  if (!rawError) {
    return { code: null, message: "Errore durante la gestione dell'abbonamento" };
  }
  if (isMissingPaymentMethodError(rawError)) {
    return { code: 'resource_missing', message: MISSING_PAYMENT_METHOD_MESSAGE };
  }
  if (typeof rawError === 'string') {
    return {
      code: null,
      message: SUBSCRIPTION_ERROR_MESSAGES[rawError] || rawError,
    };
  }
  const code = getStripeErrorCode(rawError);
  return {
    code,
    message:
      SUBSCRIPTION_ERROR_MESSAGES[code] ||
      "Errore durante la gestione dell'abbonamento",
  };
};

const createSubscription = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { clientId, priceId, returnUrl } = payload;
        let response;
        try {
          response = yield call(() =>
            paymentsApi.post('/createSubscriptions', {
              clientId,
              priceIds: [priceId],
              appEp: process.env.REACT_APP_API_URL,
              tokenApp: getActiveSalonToken(),
              returnUrl:
                returnUrl ||
                (typeof window !== 'undefined' ? window.location.href.split('#')[0] : undefined),
            }),
          );
        } catch (err) {
          const rawError = err?.response?.data?.error || err?.response?.data || err;
          yield put(settingsActions.CREATE_SUBSCRIPTION.FAILED(mapSubscriptionError(rawError)));
          return;
        }

        if (response?.data?.error) {
          yield put(
            settingsActions.CREATE_SUBSCRIPTION.FAILED(
              mapSubscriptionError(response.data.error),
            ),
          );
          return;
        }

        yield put(settingsActions.CREATE_SUBSCRIPTION.SUCCESS(response.data));
        if (response?.data?.checkoutUrl) {
          callback?.(response.data);
          return;
        }

        yield put(settingsActions.GET_SUBSCRIPTION_STATUS.REQUEST({}));
        yield put(settingsActions.GET_CUSTOMER_PRODUCTS.REQUEST({}));
        yield put(
          settingsActions.GET_CUSTOMER_INVOICE.REQUEST({ clientId }),
        );
        callback?.(response.data);
      },
    (err) => settingsActions.CREATE_SUBSCRIPTION.FAILED(mapSubscriptionError(err)),
  );

const acceptTerms = ({ callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => appApi.post('/business/auth/setAcceptedTerms', {}));
        yield put(settingsActions.ACCEPT_TERMS.SUCCESS(data));
        callback?.();
      },
    (err) => settingsActions.ACCEPT_TERMS.FAILED(err),
  );

const getAccountsStaff = ({ payload, callback, options } = {}) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => appApi.get('/business/getAccountsBusiness'));
        yield put(settingsActions.GET_ACCOUNTS_STAFF.SUCCESS(data.accounts));
        callback?.(data);
      },
    (err) => settingsActions.GET_ACCOUNTS_STAFF.FAILED(err),
    options,
  );

const addAccountStaff = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => appApi.post('/business/createAccountBusiness', payload));
        yield put(settingsActions.ADD_ACCOUNT_STAFF.SUCCESS({...payload, idUtenteBusiness: data.utenteBusiness}));
        callback?.(data);
      },
    (err) => settingsActions.ADD_ACCOUNT_STAFF.FAILED(err),
  );

const editAccountStaff = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => appApi.post('/business/updateAccountBusiness', payload));
        yield put(settingsActions.EDIT_ACCOUNT_STAFF.SUCCESS(data));
        callback?.(data);
      },
    (err) => settingsActions.EDIT_ACCOUNT_STAFF.FAILED(err),
  );

const deleteAccountStaff = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => appApi.put('/business/deleteAccountBusiness', payload));
        yield put(settingsActions.DELETE_ACCOUNT_STAFF.SUCCESS(payload));
        callback?.(data);
      },
    (err) => settingsActions.DELETE_ACCOUNT_STAFF.FAILED(err),
  );

const getMyAccountStaff = ({ payload, callback, options } = {}) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => appApi.get('/business/workers/getMyAccount'));
        yield put(settingsActions.GET_MY_ACCOUNT_STAFF.SUCCESS(data));
        localStorage.setItem('lastGetStaff', Date.now().toString());
        if(data.disabilitato !== 0) {
          yield put(settingsActions.SHOW_TOKEN_EXPIRED_MODAL.SUCCESS());
        }
        callback?.(data);
      },
    (err) => settingsActions.GET_MY_ACCOUNT_STAFF.FAILED(err),
    options,
  );

const changeFirstPwd = ({ payload, callback }) =>
  sagaAssessor(
    () =>
      function* () {
        const { data } = yield call(() => appApi.post('/business/worker/changeFirstPwd', payload));
        yield put(settingsActions.CHANGE_FIRST_PWD.SUCCESS(data));
        callback?.(data);
      },
    (err) => settingsActions.CHANGE_FIRST_PWD.FAILED(err),
  );

export default function* () {
  yield takeLatest(settingsTypes.CREATE_CUSTOMER_PORTAL.REQUEST, createCustomerPortal);
  yield takeLatest(settingsTypes.GET_CUSTOMER_BILL.REQUEST, getCustomerBill);
  yield takeLatest(settingsTypes.GET_CUSTOMER_PLAN.REQUEST, getCustomerPlan);
  yield takeLatest(settingsTypes.GET_CUSTOMER_INVOICE.REQUEST, getCustomerInvoice);
  yield takeLatest(settingsTypes.GET_CUSTOMER_PRODUCTS.REQUEST, getCustomerProducts);
  yield takeLatest(settingsTypes.CREATE_PAYMENT_PORTAL.REQUEST, createPaymentPortal);
  yield takeLatest(settingsTypes.GET_SUBSCRIPTION_STATUS.REQUEST, getSubscriptionStatus);
  yield takeLatest(settingsTypes.CREATE_SUBSCRIPTION.REQUEST, createSubscription);
  yield takeLatest(settingsTypes.ACCEPT_TERMS.REQUEST, acceptTerms);

  yield takeLatest(settingsTypes.GET_ACCOUNTS_STAFF.REQUEST, getAccountsStaff);
  yield takeLatest(settingsTypes.ADD_ACCOUNT_STAFF.REQUEST, addAccountStaff);
  yield takeLatest(settingsTypes.EDIT_ACCOUNT_STAFF.REQUEST, editAccountStaff);
  yield takeLatest(settingsTypes.DELETE_ACCOUNT_STAFF.REQUEST, deleteAccountStaff);
  yield takeLatest(settingsTypes.GET_MY_ACCOUNT_STAFF.REQUEST, getMyAccountStaff);
}
